/**
 * Cart Quantity-Variant Stepper + Unified Bulk Savings Display
 *
 * 1) For products where "Quantity" is a variant option, the Shopify cart qty
 *    is locked at 1 and +/- buttons step through variant tiers.
 *
 * 2) For ALL products (old + v2 templates), when the JS discount system
 *    populates [data-discount], reformat it to match the v2 "Bulk Savings"
 *    layout: heading + pill badge.
 *
 * FIXED 2026-08-21 (audit items 14 and 15):
 *  - Per-unit personalization is now reconciled to the new quantity. Stepping
 *    1 Sign to 3 Signs used to carry forward only "Sign points toward 1", so
 *    signs 2 and 3 reached production with no wording at all. Stepping down
 *    used to leave orphan properties for signs that no longer existed.
 *  - The page now reloads after a tier change instead of firing cart:build.
 *    The line item and total re-rendered on that event but the subtotal and
 *    the free-shipping bar did not, so the cart showed two different numbers
 *    until a manual refresh.
 *
 * CHANGED 2026-08-24 (orders #1068, #1069, #1077):
 *  - The 08-21 fix filled the new signs with the placeholder "To be confirmed"
 *    and the customer was never asked. Three orders reached production that
 *    way. Stepping UP now opens an inline prompt on the line item asking for
 *    each new sign's wording (same choices as the product page, custom
 *    wording allowed, "Leave blank" allowed). The tier swap only fires once
 *    every new sign has a value. Stepping DOWN still swaps immediately.
 *  - The placeholder is kept only as a last-resort safety net for a code
 *    path that should now be unreachable.
 */
(function () {

  // ── Per-unit property reconciliation ─────────────────────────────
  //
  // The personalizer writes one property per unit, named "<label> <n>":
  //   Sign points toward 1: Ceremony
  //   Sign points toward 2: Parking
  // Shared fields (Names, Wedding date, Notes) carry no number.
  //
  // When the tier changes, the per-unit set has to change with it.
  var PLACEHOLDER = 'To be confirmed';
  var BLANK = 'Leave blank'; // same literal the product page submits for a confirmed blank

  // Split properties into per-unit groups (base label -> {n: value}) and
  // everything else. A group only counts when unit 1 exists, so a one-off
  // shared field that happens to end in a number is left alone.
  function splitProperties(props) {
    var perUnit = {};
    var passthrough = {};

    Object.keys(props).forEach(function (k) {
      // Internal properties (_Design Help, _Customer Upload 1, _Mockup Url)
      // are never per-unit. Some end in a digit, so they must be excluded
      // before the pattern match or we would invent _Customer Upload 2..N.
      if (k.charAt(0) === '_') { passthrough[k] = props[k]; return; }

      var m = k.match(/^(.+?)\s+(\d+)$/);
      if (!m) { passthrough[k] = props[k]; return; }

      var base = m[1];
      var n = parseInt(m[2], 10);
      if (!perUnit[base]) { perUnit[base] = {}; }
      perUnit[base][n] = props[k];
    });

    var groups = {};
    Object.keys(perUnit).forEach(function (base) {
      if (perUnit[base][1] === undefined) {
        Object.keys(perUnit[base]).forEach(function (n) {
          passthrough[base + ' ' + n] = perUnit[base][n];
        });
        return;
      }
      groups[base] = perUnit[base];
    });

    return { groups: groups, passthrough: passthrough };
  }

  // Build the property set for newQty. `filled` is {base: {n: value}} with
  // the wording the customer just gave us for the added units.
  function reconcileProperties(props, newQty, filled) {
    var split = splitProperties(props);
    var out = {};
    Object.keys(split.passthrough).forEach(function (k) { out[k] = split.passthrough[k]; });

    Object.keys(split.groups).forEach(function (base) {
      var group = split.groups[base];
      var extra = (filled && filled[base]) || {};
      for (var i = 1; i <= newQty; i++) {
        var v = group[i];
        if (v === undefined || v === null || v === '') { v = extra[i]; }
        out[base + ' ' + i] = (v === undefined || v === null || v === '') ? PLACEHOLDER : v;
      }
      // Units above newQty are intentionally dropped.
    });

    return out;
  }

  // Which units would be brand new (no value yet) if we moved to newQty?
  // Returns [{base, n}] in display order.
  function missingUnits(props, newQty) {
    var split = splitProperties(props);
    var out = [];
    Object.keys(split.groups).forEach(function (base) {
      for (var i = 1; i <= newQty; i++) {
        var v = split.groups[base][i];
        if (v === undefined || v === null || v === '') { out.push({ base: base, n: i }); }
      }
    });
    return out;
  }

  // ── Helpers ──────────────────────────────────────────────────────

  function readProperties(wrapper) {
    var properties = {};
    try {
      var propsRaw = JSON.parse(wrapper.getAttribute('data-properties'));
      if (Array.isArray(propsRaw)) {
        propsRaw.forEach(function (p) {
          if (p[0] && p[1] !== '') properties[p[0]] = p[1];
        });
      } else if (propsRaw && typeof propsRaw === 'object') {
        properties = propsRaw;
      }
    } catch (e) {}
    return properties;
  }

  function readChoices(wrapper) {
    try {
      var c = JSON.parse(wrapper.getAttribute('data-dir-choices') || '[]');
      if (Array.isArray(c)) {
        return c.map(function (s) { return (s + '').trim(); }).filter(Boolean);
      }
    } catch (e) {}
    return [];
  }

  // "2 Signs" -> "Sign". Falls back to "sign".
  function unitName(wrapper) {
    var attr = (wrapper.getAttribute('data-unit') || '').trim();
    if (attr) { return attr; }
    var input = wrapper.querySelector('.js-qty__num');
    var txt = input ? (input.value || '') : '';
    var m = txt.match(/^\s*\d+\s+(.+?)\s*$/);
    if (!m) { return 'sign'; }
    var u = m[1].trim();
    if (/s$/i.test(u) && u.length > 2) { u = u.slice(0, -1); }
    return u;
  }

  function plural(n, unit) {
    return n === 1 ? unit : unit + 's';
  }

  function qtyLabel(qty, unit) {
    return qty + ' ' + plural(qty, unit);
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) { e.className = cls; }
    if (text !== undefined && text !== null) { e.textContent = text; }
    return e;
  }

  // ── Tier swap (change line to 0, add the new variant, reload) ────

  function swapTier(wrapper, tiers, currentIndex, nextTier, properties) {
    var input = wrapper.querySelector('.js-qty__num');
    var lineKey = wrapper.getAttribute('data-line-key');
    var unit = unitName(wrapper);

    if (input) { input.value = qtyLabel(nextTier.qty, unit); }
    wrapper.classList.add('is-loading');

    fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      var addData = { id: nextTier.id, quantity: 1 };
      if (Object.keys(properties).length > 0) addData.properties = properties;
      return fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [addData] })
      });
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      // Full reload. A cart:build event only refreshed the line item and the
      // total, leaving the subtotal and the free-shipping bar showing stale
      // numbers. Those are server-rendered Liquid, so re-rendering them in JS
      // would mean duplicating the threshold logic and letting it drift again.
      window.location.reload();
    })
    .catch(function (err) {
      console.error('Variant swap failed:', err);
      if (input) { input.value = qtyLabel(tiers[currentIndex].qty, unit); }
      wrapper.classList.remove('is-loading');
    });
  }

  // ── Inline "what should the new sign say?" prompt ────────────────
  //
  // Rendered inside the line item (after the qty/price row) so the customer
  // sees it right where they clicked. One prompt per line item at a time.

  function closeAsk(wrapper) {
    var ask = wrapper._hkAsk;
    if (ask && ask.parentNode) { ask.parentNode.removeChild(ask); }
    wrapper._hkAsk = null;
    wrapper.classList.remove('is-asking');
  }

  // Typable picker: a text input plus a dropdown of the choices, revealed on
  // focus via CSS :focus-within. Mirrors the product page combobox.
  function buildCombo(choices, placeholder, ariaLabel) {
    var combo = el('div', 'hk-cartask__combo');
    var inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'hk-cartask__input';
    inp.autocomplete = 'off';
    inp.placeholder = placeholder;
    inp.setAttribute('aria-label', ariaLabel);

    var caret = el('span', 'hk-cartask__caret');
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 4.5L6 7.5L9 4.5"/></svg>';

    var list = el('ul', 'hk-cartask__list');
    list.setAttribute('role', 'listbox');

    var opts = [];
    choices.forEach(function (w) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hk-cartask__opt';
      b.textContent = w;
      b.addEventListener('mousedown', function (e) {
        // mousedown keeps the input focused so the pick lands before blur
        e.preventDefault();
        inp.value = w;
        inp.dispatchEvent(new Event('input', { bubbles: true }));
        filter();
        inp.blur();
      });
      li.appendChild(b);
      list.appendChild(li);
      opts.push({ li: li, w: w });
    });

    function filter() {
      var q = inp.value.trim().toLowerCase();
      var exact = opts.some(function (o) { return o.w.toLowerCase() === q; });
      var n = 0;
      opts.forEach(function (o) {
        var show = !q || exact || o.w.toLowerCase().indexOf(q) >= 0;
        o.li.hidden = !show;
        if (show) { n++; }
      });
      combo.classList.toggle('is-empty', n === 0);
    }

    inp.addEventListener('input', filter);
    inp.addEventListener('keydown', function (e) { if (e.key === 'Escape') { inp.blur(); } });

    combo.appendChild(inp);
    combo.appendChild(caret);
    if (opts.length) { combo.appendChild(list); }
    filter();
    combo._input = inp;
    return combo;
  }

  function openAsk(wrapper, tiers, currentIndex, nextTier, properties, missing) {
    closeAsk(wrapper);

    var item = wrapper.closest('.cart__item') || wrapper.parentNode;
    var host = item.querySelector('.cart__item-details') || item;
    var unit = unitName(wrapper);
    var choices = readChoices(wrapper);
    var addCount = nextTier.qty - tiers[currentIndex].qty;
    var multiGroup = Object.keys(splitProperties(properties).groups).length > 1;

    var ask = el('div', 'hk-cartask');
    ask.setAttribute('role', 'group');

    // "Sign points toward" fields ask where the sign points; anything else
    // asks what it should say.
    var pointsToward = missing.every(function (m) { return /points? toward/i.test(m.base); });
    var q = addCount === 1
      ? (pointsToward ? 'Where should it point?' : 'What should it say?')
      : (pointsToward ? 'Where should they point?' : 'What should they say?');
    var title = el('div', 'hk-cartask__title',
      (addCount === 1
        ? 'Adding one more ' + unit.toLowerCase() + '. '
        : 'Adding ' + addCount + ' more ' + plural(addCount, unit.toLowerCase()) + '. ') + q);
    ask.appendChild(title);

    var hint = el('div', 'hk-cartask__hint',
      choices.length ? 'Pick from the list or type your own wording.' : 'Type the wording for each one.');
    ask.appendChild(hint);

    var err = el('div', 'hk-cartask__err');
    err.hidden = true;

    var rows = el('div', 'hk-cartask__rows');
    var inputs = [];
    missing.forEach(function (m, idx) {
      var row = el('div', 'hk-cartask__row');
      var tag = el('span', 'hk-cartask__n', (multiGroup ? m.base + ' ' : unit + ' ') + m.n);
      row.appendChild(tag);

      var example = choices.length ? choices[(m.n - 1) % choices.length] : '';
      var combo = buildCombo(choices, example ? 'e.g. ' + example : 'Wording', m.base + ' ' + m.n);
      row.appendChild(combo);

      var blankBtn = el('button', 'hk-cartask__blank', 'Leave blank');
      blankBtn.type = 'button';
      blankBtn.title = 'This ' + unit.toLowerCase() + ' should print with no wording here';
      blankBtn.addEventListener('click', function () {
        combo._input.value = BLANK;
        combo._input.dispatchEvent(new Event('input', { bubbles: true }));
        row.classList.remove('is-missing');
      });
      row.appendChild(blankBtn);

      combo._input.addEventListener('input', function () {
        if (combo._input.value.trim()) { row.classList.remove('is-missing'); }
        err.hidden = true;
      });

      rows.appendChild(row);
      inputs.push({ base: m.base, n: m.n, input: combo._input, row: row });
    });
    ask.appendChild(rows);
    ask.appendChild(err);

    var actions = el('div', 'hk-cartask__actions');
    var go = el('button', 'hk-cartask__go', 'Add ' + (addCount === 1 ? 'this ' + unit.toLowerCase() : addCount + ' ' + plural(addCount, unit.toLowerCase())));
    go.type = 'button';
    var cancel = el('button', 'hk-cartask__cancel', 'Cancel');
    cancel.type = 'button';
    actions.appendChild(go);
    actions.appendChild(cancel);
    ask.appendChild(actions);

    cancel.addEventListener('click', function () {
      closeAsk(wrapper);
      var input = wrapper.querySelector('.js-qty__num');
      if (input) { input.value = qtyLabel(tiers[currentIndex].qty, unit); }
    });

    go.addEventListener('click', function () {
      var filled = {};
      var firstMissing = null;
      inputs.forEach(function (it) {
        var v = it.input.value.trim();
        if (!v) {
          it.row.classList.add('is-missing');
          if (!firstMissing) { firstMissing = it; }
          return;
        }
        it.row.classList.remove('is-missing');
        if (!filled[it.base]) { filled[it.base] = {}; }
        filled[it.base][it.n] = v;
      });
      if (firstMissing) {
        err.textContent = 'Tell us what ' + (multiGroup ? firstMissing.base + ' ' : unit + ' ') + firstMissing.n + ' should say, or choose Leave blank.';
        err.hidden = false;
        firstMissing.input.focus();
        return;
      }
      go.disabled = true;
      cancel.disabled = true;
      var props = reconcileProperties(properties, nextTier.qty, filled);
      swapTier(wrapper, tiers, currentIndex, nextTier, props);
    });

    // Enter in any box behaves like the primary button.
    inputs.forEach(function (it) {
      it.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); go.click(); }
      });
    });

    host.appendChild(ask);
    wrapper._hkAsk = ask;
    wrapper.classList.add('is-asking');

    var qtyInput = wrapper.querySelector('.js-qty__num');
    if (qtyInput) { qtyInput.value = qtyLabel(nextTier.qty, unit); }

    // No auto-focus: focusing opens the choice list, which would sit on top
    // of the Add / Cancel buttons before the customer has touched anything.
    ask.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // ── Variant stepper ──────────────────────────────────────────────

  // Prevent theme's built-in qty component from initialising on variant wrappers
  var observer = new MutationObserver(function () {
    document.querySelectorAll('.js-qty__wrapper--variant .js-qty__num').forEach(function (input) {
      input.removeAttribute('name');
    });
    // Also reformat any newly-populated discount badges
    reformatDiscountBadges();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('.js-qty__adjust');
    if (!btn) return;

    var wrapper = btn.closest('.js-qty__wrapper--variant');
    if (!wrapper) return;

    evt.preventDefault();
    evt.stopPropagation();

    if (wrapper.classList.contains('is-loading')) return;

    var tiers = JSON.parse(wrapper.getAttribute('data-qty-tiers'));
    var currentVariantId = parseInt(wrapper.getAttribute('data-current-variant'), 10);

    tiers.sort(function (a, b) { return a.qty - b.qty; });

    var currentIndex = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].id === currentVariantId) {
        currentIndex = i;
        break;
      }
    }

    var isMinus = btn.classList.contains('js-qty__adjust--minus');

    // A prompt is already open for this line. Plus again: nudge the first
    // empty box. Minus: drop the prompt and step down from the real tier.
    if (wrapper._hkAsk) {
      if (!isMinus) {
        var empty = wrapper._hkAsk.querySelector('.hk-cartask__input');
        if (empty) { empty.focus(); }
        return;
      }
      closeAsk(wrapper);
    }

    var nextIndex = isMinus ? currentIndex - 1 : currentIndex + 1;

    if (nextIndex < 0 || nextIndex >= tiers.length) return;

    var nextTier = tiers[nextIndex];
    if (nextTier.id === currentVariantId) return;

    var properties = readProperties(wrapper);

    // Stepping up with per-unit personalization: ask before we swap.
    if (!isMinus) {
      var missing = missingUnits(properties, nextTier.qty);
      if (missing.length) {
        openAsk(wrapper, tiers, currentIndex, nextTier, properties, missing);
        return;
      }
    }

    // Stepping down, or a product with no per-unit wording: swap right away.
    swapTier(wrapper, tiers, currentIndex, nextTier, reconcileProperties(properties, nextTier.qty));

  }, true);


  // ── Reformat old-template discount badges ────────────────────────
  //
  // The theme's JS discount system populates small[data-discount] with
  // text like "5% Off (-$2.50)". We watch for that and restructure it
  // into the same layout the v2 template uses:
  //   BULK SAVINGS
  //   $x.xx/ea   Save X%
  //
  function reformatDiscountBadges() {
    document.querySelectorAll('small.cart__discount[data-discount]').forEach(function (el) {
      if (el.classList.contains('md-reformatted')) return;
      var text = el.textContent.trim();
      if (!text) return;

      el.classList.add('md-reformatted');

      // Build replacement markup
      var container = document.createElement('div');
      container.className = 'cart__bulk-savings-wrap';
      container.style.textAlign = 'right';
      container.style.marginTop = '6px';

      var heading = document.createElement('div');
      heading.className = 'cart__bulk-heading';
      heading.textContent = 'Bulk Savings';

      var badge = document.createElement('div');
      badge.className = 'cart__bulk-savings';
      badge.innerHTML = '<span class="cart__bulk-save-badge">' + text + '</span>';

      container.appendChild(heading);
      container.appendChild(badge);

      el.parentNode.insertBefore(container, el.nextSibling);
    });
  }

  // Run on load in case discounts are already rendered
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reformatDiscountBadges);
  } else {
    reformatDiscountBadges();
  }
  // Also run after cart rebuilds
  document.addEventListener('cart:build', function () {
    setTimeout(reformatDiscountBadges, 500);
  });

})();
